/**
 * @fileoverview Request City — subscribes a tour request to Klaviyo.
 *
 * The same submit pattern as newsletter-popup.js, without the popup half of it.
 * That file has three jobs; this one only has the third:
 *
 *   **Submit to Klaviyo and show what happened.** The client subscriptions
 *   endpoint is called straight from the browser with the public company id.
 *   A 202 is success; anything else — including a network or CORS failure,
 *   which is the reported failure mode for this endpoint — shows the error
 *   state and writes the detail to the console. Nothing fails silently except
 *   a filled honeypot, which is meant to.
 *
 * The popup's other two jobs have no counterpart here. There is no frequency
 * capping to do — this is a band in the page, not something that interrupts a
 * visitor — so nothing is written to localStorage, and there is no dialog, no
 * focus trap and no scroll lock.
 *
 * Nor is there a sessionStorage marker. newsletter-popup.js needs one because
 * Shopify reports a successful customer-form post on *every* customer form on
 * the redirected page and the footer renders one; this section renders the
 * theme's only contact form, so its Liquid markers speak for themselves. See
 * the comment at the top of sections/request-city.liquid.
 *
 * With either Klaviyo field blank the section renders Shopify's contact form
 * instead and this script is not loaded at all — that path is a full page post
 * with the browser's own required-field validation.
 *
 * What this file adds over the popup's submit is the second field. Klaviyo
 * profiles take arbitrary custom properties, so the requested city rides along
 * on the same request as a profile property rather than needing a second call.
 * The property name is a merchant setting, since it is the thing the list gets
 * segmented on.
 */

(function () {
  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  // Klaviyo pins its API by date. Changing this is an API upgrade, not a tweak.
  // Kept in step with newsletter-popup.js on purpose — two revisions against
  // one account is a migration waiting to be half-done.
  const KLAVIYO_REVISION = '2024-10-15';
  // 202 Accepted is the documented success response — the subscription is
  // queued, not completed, so there is no body to read.
  const KLAVIYO_ACCEPTED = 202;

  const instances = new WeakMap();

  class RequestCity {
    constructor(section) {
      this.section = section;

      this.form = section.querySelector('[data-request-city-form]');
      this.city = section.querySelector('[data-request-city-city]');
      this.email = section.querySelector('[data-request-city-email]');
      this.honeypot = section.querySelector('[data-request-city-honeypot]');
      this.submit = section.querySelector('[data-request-city-submit]');
      this.message = section.querySelector('[data-request-city-message]');
      // Permanently rendered, never hidden — see the Liquid for why the visible
      // message and the announced one are two different nodes.
      this.live = section.querySelector('[data-request-city-live]');
      this.success = section.querySelector('[data-request-city-success]');

      this.company = section.dataset.requestCityCompany || '';
      this.list = section.dataset.requestCityList || '';
      this.source = section.dataset.requestCitySource || '';
      this.property = section.dataset.requestCityProperty || '';
      this.errorText = section.dataset.requestCityError || '';
      this.invalidText = section.dataset.requestCityInvalid || '';
      this.missingCityText = section.dataset.requestCityMissingCity || '';

      this.onSubmit = this.handleSubmit.bind(this);

      this.form?.addEventListener('submit', this.onSubmit);
    }

    /**
     * The visible paragraph and the live region are written separately.
     *
     * The live region is never hidden and never replaced — it is in the
     * accessibility tree before, during and after the write, which is the whole
     * reason it is its own node. Writing into a region that is still `hidden`
     * and unhiding it afterwards means the mutation lands while the region is
     * out of the tree and the announcement is unreliable (VoiceOver/Safari most
     * visibly). The visible paragraph carries no live-region role, so toggling
     * `hidden` on it announces nothing and cannot double up.
     */
    setMessage(text, isError) {
      if (this.message) {
        this.message.textContent = text || '';
        this.message.classList.toggle('request-city__message--is-error', Boolean(text && isError));
        this.message.hidden = !text;
      }

      if (this.live) this.live.textContent = text || '';
    }

    /**
     * An error about a field belongs on the field as well as in the message — a
     * screen reader user who moves back to the input after the announcement
     * would otherwise find a control that reports nothing wrong with it.
     *
     * Both fields share the one message node, so the flag is cleared from both
     * and set on whichever one is being complained about.
     */
    setFieldInvalid(field) {
      [this.city, this.email].forEach((input) => {
        if (!input) return;
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      });

      if (!field) return;

      field.setAttribute('aria-invalid', 'true');
      if (this.message?.id) field.setAttribute('aria-describedby', this.message.id);
    }

    setPending(isPending) {
      if (!this.submit) return;

      this.submit.setAttribute('aria-busy', isPending ? 'true' : 'false');
      this.submit.disabled = isPending;

      const pending = this.submit.dataset.requestCityPending;
      const label = this.submit.dataset.requestCityLabel;
      if (isPending && pending) {
        this.submit.textContent = pending;
      } else if (!isPending && label) {
        this.submit.textContent = label;
      }
    }

    handleSubmit(event) {
      event.preventDefault();

      // A filled honeypot is a bot. It gets the same non-answer as a successful
      // pass — no message, no request, no clue about what gave it away.
      if (this.honeypot && this.honeypot.value.trim() !== '') return;

      const city = (this.city?.value || '').trim();
      const email = (this.email?.value || '').trim();

      // The form carries `novalidate` so these messages, not the browser's
      // bubbles, are what a visitor sees — and so they land in the live region.
      //
      // City is checked first because it is first in the reading order, and
      // only when the field is rendered at all: a merchant who clears the City
      // Field Label gets an email-only form, and demanding a city that has no
      // input would deadlock it.
      if (this.city && !city) {
        this.setMessage(this.missingCityText, true);
        this.setFieldInvalid(this.city);
        this.city.focus();
        return;
      }

      if (!email || !this.email?.checkValidity()) {
        this.setMessage(this.invalidText, true);
        this.setFieldInvalid(this.email);
        this.email?.focus();
        return;
      }

      this.setPending(true);
      this.setMessage('', false);
      this.setFieldInvalid(null);

      this.subscribe(email, city)
        .then((response) => {
          if (response.status === KLAVIYO_ACCEPTED) {
            this.showSuccess();
            return;
          }

          // Read the body before reporting: Klaviyo returns a JSON:API error
          // document, and its `detail` is the only thing that says *why*.
          return response
            .text()
            .catch(() => '')
            .then((body) => {
              console.error(
                '[request-city] Klaviyo subscription failed.',
                'status:',
                response.status,
                'body:',
                body
              );
              this.setPending(false);
              this.setMessage(this.errorText, true);
            });
        })
        .catch((error) => {
          // fetch only rejects on a network-level failure, and for this
          // endpoint that overwhelmingly means the request was blocked before
          // it left the browser — CORS, an ad/tracker blocker, or an offline
          // device. The status is unreadable in that case by design, hence the
          // hint: there is no response object to inspect.
          console.error(
            '[request-city] Klaviyo request did not complete — likely CORS, a content blocker, or no network.',
            'company_id:',
            this.company,
            'list:',
            this.list,
            error
          );
          this.setPending(false);
          this.setMessage(this.errorText, true);
        });
    }

    /**
     * POST to Klaviyo's client subscriptions endpoint. Public company id in the
     * query string, no auth header — a private key must never appear in a theme.
     *
     * The city goes in as a custom profile property rather than in the profile's
     * `location.city`: `location` is where the person *is*, written by Klaviyo's
     * own IP and order data, and a request to be toured to is not that. A named
     * custom property is also what the merchant segments on, which is why the
     * name is a setting rather than a constant here.
     *
     * `properties` is omitted entirely when there is no city to send — a
     * property set to an empty string is a real value in Klaviyo and would
     * blank out a city the profile had already given.
     */
    subscribe(email, city) {
      const profileAttributes = { email: email };

      if (city && this.property) {
        profileAttributes.properties = { [this.property]: city };
      }

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.source,
            profile: {
              data: {
                type: 'profile',
                attributes: profileAttributes,
              },
            },
          },
          relationships: {
            list: {
              data: { type: 'list', id: this.list },
            },
          },
        },
      };

      return fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.company)}`, {
        method: 'POST',
        headers: {
          revision: KLAVIYO_REVISION,
          'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    }

    showSuccess() {
      this.setPending(false);
      this.setMessage('', false);
      this.setFieldInvalid(null);

      // The thank-you replaces the form rather than joining it: leaving live
      // fields under a "we have your request" line invites a second submission.
      if (this.form) this.form.hidden = true;
      if (this.success) {
        this.success.hidden = false;
        // Moved into the live region's place in the reading order and focused,
        // so the outcome reaches a screen reader whose focus is still on the
        // submit button that has just disappeared.
        this.success.setAttribute('tabindex', '-1');
        this.success.focus();
      }
    }

    teardown() {
      this.form?.removeEventListener('submit', this.onSubmit);
    }
  }

  function initAll(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-request-city]').forEach((section) => {
      // No Klaviyo credentials means the Shopify contact form is what rendered,
      // and nothing here applies to it. The script is not even loaded on that
      // path — this is the belt to that braces, for a section re-rendered by
      // the editor after the List ID was cleared.
      if (!section.dataset.requestCityList || !section.dataset.requestCityCompany) return;
      if (instances.has(section)) return;
      instances.set(section, new RequestCity(section));
    });
  }

  function destroy(section) {
    const instance = instances.get(section);
    if (!instance) return;
    instance.teardown();
    instances.delete(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-request-city]').forEach(destroy);
  });
})();
